import React, { createContext, useContext, useState, ReactNode } from 'react';
import { FormSection } from '@/components/wizards/blueprints/types';

interface LensesContextType {
  selectedRole: string | null;
  previewMode: boolean;
  setSelectedRole: (roleId: string | null) => void;
  setPreviewMode: (enabled: boolean) => void;
  // Blueprint state for direct manipulation
  blueprintSections?: FormSection[];
  toggleSectionForRole?: (sectionId: string, roleId: string) => void;
  toggleFieldForRole?: (sectionId: string, fieldId: string, roleId: string) => void;
  setSectionVisibilityState?: (sectionId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void;
  setFieldVisibilityState?: (sectionId: string, fieldId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void;
  resetToInitialData?: () => void;
  // Registration functions for wizard to provide its state
  registerBlueprintState: (
    sections: FormSection[],
    toggleSection: (sectionId: string, roleId: string) => void,
    toggleField: (sectionId: string, fieldId: string, roleId: string) => void,
    setSectionVisibility: (sectionId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void,
    setFieldVisibility: (sectionId: string, fieldId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void,
    resetToInitial: () => void
  ) => void;
  unregisterBlueprintState: () => void;
}

const LensesContext = createContext<LensesContextType | undefined>(undefined);

interface LensesProviderProps {
  children: ReactNode;
  defaultRoleId?: string | null;
}

export function LensesProvider({ 
  children, 
  defaultRoleId = null,
}: LensesProviderProps) {
  const [selectedRole, setSelectedRole] = useState<string | null>(defaultRoleId);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [blueprintSections, setBlueprintSections] = useState<FormSection[] | undefined>(undefined);
  const [toggleSectionForRole, setToggleSectionForRole] = useState<((sectionId: string, roleId: string) => void) | undefined>(undefined);
  const [toggleFieldForRole, setToggleFieldForRole] = useState<((sectionId: string, fieldId: string, roleId: string) => void) | undefined>(undefined);
  const [setSectionVisibilityState, setSetSectionVisibilityState] = useState<((sectionId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void) | undefined>(undefined);
  const [setFieldVisibilityState, setSetFieldVisibilityState] = useState<((sectionId: string, fieldId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void) | undefined>(undefined);
  const [resetToInitialData, setResetToInitialData] = useState<(() => void) | undefined>(undefined);



  const registerBlueprintState = (
    sections: FormSection[],
    toggleSection: (sectionId: string, roleId: string) => void,
    toggleField: (sectionId: string, fieldId: string, roleId: string) => void,
    setSectionVisibility: (sectionId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void,
    setFieldVisibility: (sectionId: string, fieldId: string, roleId: string, state: 'visible-editable' | 'visible-readonly' | 'hidden') => void,
    resetToInitial: () => void
  ) => {
    setBlueprintSections(sections);
    // Wrap in arrow function to preserve the function reference
    setToggleSectionForRole(() => toggleSection);
    setToggleFieldForRole(() => toggleField);
    setSetSectionVisibilityState(() => setSectionVisibility);
    setSetFieldVisibilityState(() => setFieldVisibility);
    setResetToInitialData(() => resetToInitial);
  };

  const unregisterBlueprintState = () => {
    setBlueprintSections(undefined);
    setToggleSectionForRole(undefined);
    setToggleFieldForRole(undefined);
    setSetSectionVisibilityState(undefined);
    setSetFieldVisibilityState(undefined);
    setResetToInitialData(undefined);
    // NOTE: We intentionally DO NOT reset previewMode or selectedRole here
    // These should persist even when the wizard unmounts/remounts
    // This prevents the preview state from being lost during component re-renders
  };

  return (
    <LensesContext.Provider
      value={{
        selectedRole,
        previewMode,
        setSelectedRole,
        setPreviewMode,
        blueprintSections,
        toggleSectionForRole,
        toggleFieldForRole,
        setSectionVisibilityState,
        setFieldVisibilityState,
        resetToInitialData,
        registerBlueprintState,
        unregisterBlueprintState,
      }}
    >
      {children}
    </LensesContext.Provider>
  );
}

export function useLenses() {
  const context = useContext(LensesContext);
  if (context === undefined) {
    throw new Error('useLenses must be used within a LensesProvider');
  }
  return context;
}

// Optional hook that returns null if not in a LensesProvider (for components that may or may not be in the context)
export function useLensesOptional() {
  return useContext(LensesContext);
}

